import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Icon } from '../common/Icon';
import { FaMicrophone, FaStop, FaPlay, FaTrash, FaUpload, FaEdit } from 'react-icons/fa';
import { useUser } from '../../contexts/UserContext';
import { voiceService, toMp3WithDuration } from '../../services/voiceService';
import { scriptService, Voice } from '../../services/scriptService';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';

// Hard bounds (lenient); recommended range is 10–15s.
const MIN_SECONDS = 5;
const MAX_SECONDS = 20;
const REC_MIN = 10;
const REC_MAX = 15;

// Suggested script for the user to read aloud while recording their clip
const EXAMPLE_SCRIPT = 'Find a comfortable position, either lying down or sitting, and allow your hands to rest where they feel natural.';

type Source = 'record' | 'upload';
type Method = 'clone' | 'design';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Section = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 30px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 1.4rem;
  color: #ffffff;
`;

const Subtle = styled.p`
  margin: 0 0 18px;
  color: #aaaaaa;
  font-size: 0.92rem;
  line-height: 1.5;
`;

const VoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VoiceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: #1f1f1f;
`;

const VoiceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const VoiceName = styled.span`
  font-weight: 600;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Badge = styled.span<{ $public?: boolean }>`
  font-size: 0.72rem;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $public }) => ($public ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255,255,255,0.1)')};
  color: ${({ $public }) => ($public ? '#7bd88f' : '#aaaaaa')};
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.2s;
  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SourceToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? '#4CAF50' : '#444')};
  background: ${({ $active }) => ($active ? 'rgba(76, 175, 80, 0.15)' : '#1a1a1a')};
  color: #ffffff;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
`;

const RecordArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
`;

const ScriptPrompt = styled.div`
  margin: 4px 0 16px;
  padding: 14px 16px;
  border-left: 3px solid #4CAF50;
  border-radius: 6px;
  background: rgba(76, 175, 80, 0.08);
  color: #dddddd;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ScriptPromptLabel = styled.div`
  color: #7bd88f;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const RecordButton = styled.button<{ $recording: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 999px;
  border: none;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $recording }) => ($recording ? '#e74c3c' : '#4CAF50')};
  &:hover { filter: brightness(1.08); }
`;

const Timer = styled.span<{ $warn?: boolean }>`
  font-variant-numeric: tabular-nums;
  font-size: 1.1rem;
  color: ${({ $warn }) => ($warn ? '#e6a23c' : '#ffffff')};
`;

const PrimaryButton = styled.button`
  padding: 12px 20px;
  border-radius: 6px;
  border: none;
  background: #4CAF50;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover:not(:disabled) { background: #45a049; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 6px;
  border: 1px solid #444;
  background: #1a1a1a;
  color: #ffffff;
  cursor: pointer;
  &:hover:not(:disabled) { border-color: #666; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 600;
  color: #ffffff;
  margin: 18px 0 6px;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #444;
  background: #1a1a1a;
  color: #ffffff;
  font-size: 1rem;
  &:focus { outline: none; border-color: #4CAF50; }
  &::placeholder { color: #888; }
`;

const VoiceSelect = styled.select`
  width: 100%;
  max-width: 360px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #444;
  background: #1a1a1a;
  color: #ffffff;
  font-size: 1rem;
  cursor: pointer;
  &:focus { outline: none; border-color: #4CAF50; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const DefaultVoiceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DescriptionTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #444;
  background: #1a1a1a;
  color: #ffffff;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  &:focus { outline: none; border-color: #4CAF50; }
  &::placeholder { color: #888; }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  font-size: 0.9rem;
  color: #cccccc;
  line-height: 1.5;
  cursor: pointer;

  input { margin-top: 3px; }
  a { color: #7bd88f; }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Message = styled.div<{ $type: 'error' | 'success' | 'info' }>`
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  background: ${({ $type }) =>
    $type === 'error' ? 'rgba(231,76,60,0.18)' : $type === 'success' ? 'rgba(76,175,80,0.18)' : 'rgba(255,255,255,0.06)'};
  color: ${({ $type }) => ($type === 'error' ? '#ff8b7d' : $type === 'success' ? '#7bd88f' : '#cccccc')};
`;

const StepActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const QuotaNote = styled.span`
  color: #888888;
  font-size: 0.85rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalCard = styled.div`
  background: #2a2a2a;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
  padding: 26px;
  width: 100%;
  max-width: 460px;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  color: #ffffff;
  font-size: 1.25rem;
`;

const Notice = styled.div`
  margin: 16px 0;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: #bbbbbb;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const DangerButton = styled.button`
  padding: 10px 16px;
  border-radius: 6px;
  border: 1px solid #dc3545;
  background: transparent;
  color: #ff8b7d;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(220, 53, 69, 0.15); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const Spacer = styled.div`
  flex: 1;
`;

const MyVoiceTab: React.FC = () => {
  const { user, refreshUser } = useUser();
  const recorder = useVoiceRecorder();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const [voices, setVoices] = useState<Voice[]>([]);
  const [limit, setLimit] = useState(0);
  const [used, setUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Default-voice section (all users)
  const [availableVoices, setAvailableVoices] = useState<Voice[]>([]);
  const [savingDefault, setSavingDefault] = useState(false);
  const defaultVoiceId = user?.profile_settings?.default_voice_id || '';

  const [method, setMethod] = useState<Method>('clone');
  const [source, setSource] = useState<Source>('record');
  const [processing, setProcessing] = useState(false);
  const [audioRef, setAudioRef] = useState<string | null>(null);
  const [durationWarning, setDurationWarning] = useState('');
  const [description, setDescription] = useState('');

  const [previewing, setPreviewing] = useState(false);
  const [sampleAudio, setSampleAudio] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [allowOthers, setAllowOthers] = useState(true);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'info'; text: string } | null>(null);

  // Edit modal state
  const [editingVoice, setEditingVoice] = useState<Voice | null>(null);
  const [editName, setEditName] = useState('');
  const [editAllowOthers, setEditAllowOthers] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const canAddMore = limit === -1 || used < limit;

  const loadVoices = useCallback(async () => {
    setLoadError(false);
    try {
      const data = await voiceService.getMyVoices();
      setVoices(data.voices);
      setLimit(data.limit);
      setUsed(data.used);
    } catch (e) {
      console.error('Failed to load voices:', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVoices(); }, [loadVoices]);

  // Load all voices the user can pick as a default (public + their own)
  const loadAvailableVoices = useCallback(async () => {
    try {
      const all = await scriptService.getUserVoices();
      setAvailableVoices(all);
    } catch (e) {
      console.error('Failed to load available voices:', e);
    }
  }, []);

  useEffect(() => { loadAvailableVoices(); }, [loadAvailableVoices]);

  // Single shared player so a new play (or a re-record) always stops the previous clip
  const stopCurrentAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
  }, []);

  const playSample = useCallback((url?: string | null) => {
    if (!url) return;
    stopCurrentAudio();
    const audio = new Audio(url);
    currentAudioRef.current = audio;
    audio.play().catch(() => { /* ignore */ });
  }, [stopCurrentAudio]);

  // Stop playback when the component unmounts
  useEffect(() => () => stopCurrentAudio(), [stopCurrentAudio]);

  const resetForm = useCallback(() => {
    setAudioRef(null);
    setSampleAudio(null);
    setDurationWarning('');
    setDescription('');
    setName('');
    setAllowOthers(true);
    setConsent(false);
    recorder.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [recorder]);

  // Switch between "clone" and "design" — clears any in-progress staged state
  const switchMethod = useCallback((next: Method) => {
    setMethod(next);
    setSource('record');
    resetForm();
  }, [resetForm]);

  // Set (or clear) the default voice and play a quick sample
  const handleSetDefault = useCallback(async (voiceId: string) => {
    setSavingDefault(true);
    try {
      await voiceService.setDefaultVoice(voiceId || null);
      await refreshUser();
      if (voiceId) {
        const v = availableVoices.find(av => av.id === voiceId);
        if (v?.sampleAudio) playSample(v.sampleAudio);
      }
    } catch (e) {
      console.error('Set default voice failed:', e);
    } finally {
      setSavingDefault(false);
    }
  }, [availableVoices, playSample, refreshUser]);

  // Convert a raw clip to MP3, validate length, and upload as the reference
  const processClip = useCallback(async (blob: Blob) => {
    setMessage(null);
    setProcessing(true);
    setAudioRef(null);
    setSampleAudio(null);
    setDurationWarning('');
    try {
      const { mp3, duration } = await toMp3WithDuration(blob);
      if (duration < MIN_SECONDS) {
        setMessage({ type: 'error', text: `That clip is too short (${duration.toFixed(1)}s). Aim for ${REC_MIN}–${REC_MAX} seconds.` });
        return;
      }
      if (duration > MAX_SECONDS) {
        setMessage({ type: 'error', text: `That clip is too long (${duration.toFixed(1)}s). Aim for ${REC_MIN}–${REC_MAX} seconds.` });
        return;
      }
      if (duration < REC_MIN || duration > REC_MAX) {
        setDurationWarning(`For best results, aim for ${REC_MIN}–${REC_MAX} seconds (this clip is ${duration.toFixed(1)}s).`);
      }
      const ref = await voiceService.uploadReference(mp3);
      setAudioRef(ref);
    } catch (e: any) {
      console.error('Clip processing failed:', e);
      setMessage({ type: 'error', text: e?.message || 'Could not process that audio clip.' });
    } finally {
      setProcessing(false);
    }
  }, []);

  // Discard any staged/uploaded clip so the preview + save section hides again
  const clearStagedClip = useCallback(() => {
    setAudioRef(null);
    setSampleAudio(null);
    setDurationWarning('');
    setMessage(null);
  }, []);

  // Starting a new recording invalidates the previously staged clip/preview
  const handleStartRecording = useCallback(() => {
    stopCurrentAudio();
    clearStagedClip();
    recorder.start();
  }, [stopCurrentAudio, clearStagedClip, recorder]);

  const handleUseRecording = useCallback(() => {
    if (recorder.recordedBlob) processClip(recorder.recordedBlob);
  }, [recorder.recordedBlob, processClip]);

  const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processClip(file);
  }, [processClip]);

  const handlePreview = useCallback(async () => {
    if (!audioRef) return;
    setMessage(null);
    setPreviewing(true);
    try {
      const result = await voiceService.preview(audioRef);
      setSampleAudio(result.sampleAudio);
      playSample(result.sampleAudio);
    } catch (e: any) {
      console.error('Preview failed:', e);
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to generate preview.' });
    } finally {
      setPreviewing(false);
    }
  }, [audioRef, playSample]);

  // Generate a preview from the voice description (design mode)
  const handleDesignPreview = useCallback(async () => {
    if (!description.trim()) return;
    setMessage(null);
    setPreviewing(true);
    try {
      const result = await voiceService.designPreview(description.trim());
      setAudioRef(result.audioRef ?? null);
      setSampleAudio(result.sampleAudio);
      playSample(result.sampleAudio);
    } catch (e: any) {
      console.error('Design preview failed:', e);
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to generate preview.' });
    } finally {
      setPreviewing(false);
    }
  }, [description, playSample]);

  const handleSave = useCallback(async () => {
    if (!sampleAudio || !name.trim() || !audioRef) return;
    if (method === 'clone' && !consent) return;
    if (method === 'design' && !description.trim()) return;
    setMessage(null);
    setSaving(true);
    try {
      if (method === 'design') {
        await voiceService.createVoice({ name: name.trim(), description: description.trim(), audioRef, sampleAudio, allowOthers });
      } else {
        await voiceService.createVoice({ name: name.trim(), audioRef, sampleAudio, allowOthers, consent });
      }
      setMessage({ type: 'success', text: 'Voice saved!' });
      resetForm();
      await loadVoices();
      await refreshUser();
    } catch (e: any) {
      console.error('Save voice failed:', e);
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to save voice.' });
    } finally {
      setSaving(false);
    }
  }, [method, audioRef, sampleAudio, name, description, allowOthers, consent, resetForm, loadVoices, refreshUser]);

  const openEdit = useCallback((voice: Voice) => {
    stopCurrentAudio();
    setEditError('');
    setEditingVoice(voice);
    setEditName(voice.name);
    setEditAllowOthers(!voice.isPrivate);
  }, [stopCurrentAudio]);

  const closeEdit = useCallback(() => {
    if (editSaving) return;
    setEditingVoice(null);
    setEditError('');
  }, [editSaving]);

  const handleUpdateVoice = useCallback(async () => {
    if (!editingVoice || !editName.trim()) return;
    setEditError('');
    setEditSaving(true);
    try {
      await voiceService.updateVoice(editingVoice.id, { name: editName.trim(), allowOthers: editAllowOthers });
      setEditingVoice(null);
      await loadVoices();
    } catch (e: any) {
      console.error('Update voice failed:', e);
      setEditError(e?.response?.data?.error || 'Failed to update voice.');
    } finally {
      setEditSaving(false);
    }
  }, [editingVoice, editName, editAllowOthers, loadVoices]);

  const handleDeleteVoice = useCallback(async () => {
    if (!editingVoice) return;
    if (!window.confirm(`Delete "${editingVoice.name}"? This cannot be undone.`)) return;
    setEditError('');
    setEditSaving(true);
    try {
      await voiceService.deleteVoice(editingVoice.id);
      setEditingVoice(null);
      await loadVoices();
      await refreshUser();
    } catch (e: any) {
      console.error('Delete voice failed:', e);
      setEditError(e?.response?.data?.error || 'Failed to delete voice.');
    } finally {
      setEditSaving(false);
    }
  }, [editingVoice, loadVoices, refreshUser]);


  const canSave = method === 'design'
    ? (!!audioRef && !!sampleAudio && !!name.trim() && !!description.trim() && !saving)
    : (!!audioRef && !!sampleAudio && !!name.trim() && consent && !saving);

  // Shared name + sharing (+ consent for clones) + save controls
  const renderFinalize = () => (
    <>
      <FieldLabel htmlFor="voice-name">Voice name</FieldLabel>
      <NameInput
        id="voice-name"
        type="text"
        value={name}
        maxLength={255}
        placeholder="e.g. My calm voice"
        onChange={(e) => setName(e.target.value)}
      />

      <CheckboxRow>
        <input type="checkbox" checked={allowOthers} onChange={(e) => setAllowOthers(e.target.checked)} />
        <span>Allow others to use this voice</span>
      </CheckboxRow>

      {method === 'clone' && (
        <CheckboxRow>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>
            I certify this is my voice, or I have permission to use this voice, and I grant full non-exclusive
            rights for this voice to be used as described in the{' '}
            <Link to="/tos" target="_blank">Terms of Service</Link> and{' '}
            <Link to="/privacy" target="_blank">Privacy Policy</Link>.
          </span>
        </CheckboxRow>
      )}

      <StepActions>
        <PrimaryButton onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save voice'}
        </PrimaryButton>
        {!sampleAudio && <QuotaNote>Generate a preview before saving.</QuotaNote>}
      </StepActions>
    </>
  );

  // Default-voice selector — available to all users
  const defaultVoiceSection = (
    <Section>
      <SectionTitle>Default Voice</SectionTitle>
      <Subtle>Choose the voice that's pre-selected when you create a meditation — any shared voice or one of your own.</Subtle>
      <DefaultVoiceRow>
        <VoiceSelect
          value={defaultVoiceId}
          disabled={savingDefault || availableVoices.length === 0}
          onChange={(e) => handleSetDefault(e.target.value)}
        >
          <option value="">No default (use first available)</option>
          {availableVoices.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </VoiceSelect>
        {(() => {
          const dv = availableVoices.find(v => v.id === defaultVoiceId);
          return dv?.sampleAudio ? (
            <IconButton title="Play sample" onClick={() => playSample(dv.sampleAudio)}>
              <Icon icon={FaPlay} />
            </IconButton>
          ) : null;
        })()}
      </DefaultVoiceRow>
    </Section>
  );

  return (
    <Container>
      {defaultVoiceSection}

      {loading ? (
        <Section><Subtle style={{ margin: 0 }}>Loading…</Subtle></Section>
      ) : loadError ? (
        <Section>
          <SectionTitle>Your Voices</SectionTitle>
          <Subtle style={{ margin: 0 }}>We couldn't load your voices right now. Please refresh and try again.</Subtle>
        </Section>
      ) : limit === 0 ? (
        <Section>
          <SectionTitle>Custom Voices</SectionTitle>
          <Subtle>
            Custom voices are not available on your current subscription. Please{' '}
            <Link to="/subscription">upgrade</Link> to be able to personalize your meditations with your own voice.
          </Subtle>
        </Section>
      ) : (
        <>
          <Section>
            <SectionTitle>Your Voices</SectionTitle>
        <Subtle>
          {limit === -1
            ? 'Create and manage your custom voices.'
            : `You have used ${used} of ${limit} custom ${limit === 1 ? 'voice' : 'voices'}.`}
        </Subtle>

        {voices.length === 0 ? (
          <Subtle style={{ margin: 0 }}>You haven't created any voices yet.</Subtle>
        ) : (
          <VoiceList>
            {voices.map(voice => (
              <VoiceRow key={voice.id}>
                <VoiceMeta>
                  <VoiceName>{voice.name}</VoiceName>
                  <Badge $public={!voice.isPrivate}>{voice.isPrivate ? 'Private' : 'Shared'}</Badge>
                </VoiceMeta>
                <RowActions>
                  <IconButton title="Play sample" onClick={() => playSample(voice.sampleAudio)} disabled={!voice.sampleAudio}>
                    <Icon icon={FaPlay} />
                  </IconButton>
                  <IconButton title="Edit" onClick={() => openEdit(voice)}>
                    <Icon icon={FaEdit} />
                  </IconButton>
                </RowActions>
              </VoiceRow>
            ))}
          </VoiceList>
        )}
      </Section>

      {canAddMore ? (
        <Section>
          <SectionTitle>Add a Voice</SectionTitle>

          <SourceToggle>
            <ToggleButton $active={method === 'clone'} onClick={() => switchMethod('clone')}>Clone a voice</ToggleButton>
            <ToggleButton $active={method === 'design'} onClick={() => switchMethod('design')}>Design a voice</ToggleButton>
          </SourceToggle>

          {method === 'clone' ? (
            <>
              <Subtle>Record or upload a clear {REC_MIN}–{REC_MAX} second clip of the voice speaking naturally.</Subtle>

              <ScriptPrompt>
                <ScriptPromptLabel>Read this aloud</ScriptPromptLabel>
                {EXAMPLE_SCRIPT}
              </ScriptPrompt>

              <SourceToggle>
                <ToggleButton $active={source === 'record'} onClick={() => { setSource('record'); clearStagedClip(); }}>Record</ToggleButton>
                <ToggleButton $active={source === 'upload'} onClick={() => { setSource('upload'); clearStagedClip(); }}>Upload</ToggleButton>
              </SourceToggle>

              {source === 'record' ? (
                <RecordArea>
                  {recorder.isRecording ? (
                    <>
                      <Timer $warn={recorder.seconds < REC_MIN}>{recorder.seconds}s / {recorder.maxSeconds}s</Timer>
                      <RecordButton $recording onClick={recorder.stop}>
                        <Icon icon={FaStop} /> Stop
                      </RecordButton>
                    </>
                  ) : (
                    <RecordButton $recording={false} onClick={handleStartRecording}>
                      <Icon icon={FaMicrophone} /> {recorder.recordedUrl ? 'Re-record' : 'Start recording'}
                    </RecordButton>
                  )}
                  {recorder.error && <Message $type="error">{recorder.error}</Message>}
                  {recorder.recordedUrl && !recorder.isRecording && (
                    <StepActions>
                      <SecondaryButton onClick={() => playSample(recorder.recordedUrl)}>
                        <Icon icon={FaPlay} /> Play
                      </SecondaryButton>
                      <PrimaryButton onClick={handleUseRecording} disabled={processing}>
                        {processing ? 'Processing…' : 'Use this clip'}
                      </PrimaryButton>
                    </StepActions>
                  )}
                </RecordArea>
              ) : (
                <RecordArea>
                  <HiddenFileInput ref={fileInputRef} type="file" accept="audio/*" onChange={handleFileSelected} />
                  <PrimaryButton onClick={() => fileInputRef.current?.click()} disabled={processing}>
                    <Icon icon={FaUpload} /> {processing ? 'Processing…' : 'Choose audio file'}
                  </PrimaryButton>
                </RecordArea>
              )}

              {durationWarning && <Message $type="info">{durationWarning}</Message>}

              {audioRef && (
                <>
                  <StepActions>
                    <PrimaryButton onClick={handlePreview} disabled={previewing}>
                      {previewing ? 'Generating…' : sampleAudio ? 'Regenerate preview' : 'Generate preview'}
                    </PrimaryButton>
                    {sampleAudio && (
                      <SecondaryButton onClick={() => playSample(sampleAudio)}>
                        <Icon icon={FaPlay} /> Play preview
                      </SecondaryButton>
                    )}
                  </StepActions>
                  {renderFinalize()}
                </>
              )}
            </>
          ) : (
            <>
              <Subtle>Describe the voice you want in plain language and we'll generate it.</Subtle>

              <FieldLabel htmlFor="voice-desc">Voice description</FieldLabel>
              <DescriptionTextArea
                id="voice-desc"
                value={description}
                maxLength={1000}
                placeholder="e.g. A warm, soothing female voice with a calm, slow, meditative tone and a gentle British accent"
                onChange={(e) => { setDescription(e.target.value); setSampleAudio(null); setAudioRef(null); }}
              />

              <StepActions>
                <PrimaryButton onClick={handleDesignPreview} disabled={previewing || !description.trim()}>
                  {previewing ? 'Generating…' : sampleAudio ? 'Regenerate preview' : 'Generate preview'}
                </PrimaryButton>
                {sampleAudio && (
                  <SecondaryButton onClick={() => playSample(sampleAudio)}>
                    <Icon icon={FaPlay} /> Play preview
                  </SecondaryButton>
                )}
              </StepActions>

              {sampleAudio && renderFinalize()}
            </>
          )}

          {message && <Message $type={message.type}>{message.text}</Message>}
        </Section>
      ) : (
        <Section>
          <SectionTitle>Voice limit reached</SectionTitle>
          <Subtle>
            You've used all {limit} of your custom voices. Delete one to add another, or{' '}
            <Link to="/subscription">upgrade your plan</Link> for more.
          </Subtle>
          {message && <Message $type={message.type}>{message.text}</Message>}
        </Section>
      )}
        </>
      )}

      {editingVoice && (
        <ModalOverlay onClick={closeEdit}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit Voice</ModalTitle>

            <FieldLabel htmlFor="edit-voice-name">Voice name</FieldLabel>
            <NameInput
              id="edit-voice-name"
              type="text"
              value={editName}
              maxLength={255}
              placeholder="Voice name"
              onChange={(e) => setEditName(e.target.value)}
            />

            <CheckboxRow>
              <input type="checkbox" checked={editAllowOthers} onChange={(e) => setEditAllowOthers(e.target.checked)} />
              <span>Allow others to use this voice</span>
            </CheckboxRow>

            <Notice>
              Making a voice private or deleting it will not remove meditations that have already been created using it.
            </Notice>

            {editError && <Message $type="error">{editError}</Message>}

            <ModalActions>
              <DangerButton onClick={handleDeleteVoice} disabled={editSaving}>
                <Icon icon={FaTrash} /> Delete
              </DangerButton>
              <Spacer />
              <SecondaryButton onClick={closeEdit} disabled={editSaving}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handleUpdateVoice} disabled={editSaving || !editName.trim()}>
                {editSaving ? 'Saving…' : 'Save'}
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default MyVoiceTab;
